import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

interface Fund {
  id: string;
  name: string;
  asset_class: string;
  strategy: string;
  status: string;
  aum_min: number;
  aum_max: number;
  inception_date: string;
  management_fee: number;
  performance_fee: number;
  minimum_subscription: number;
}

interface FundStats {
  fund_id: string;
  connections: number;
  latest_return: number | null;
  latest_month: string | null;
  months_of_data: number;
}

const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund: { color: '#0D6E8A', bg: '#0D6E8A15' },
  pe: { color: '#92640A', bg: '#92640A15' },
  vc: { color: '#C9A84C', bg: '#FDF6E7' },
  re: { color: '#C0222A', bg: '#C0222A15' },
  credit: { color: '#6fa8dc', bg: 'rgba(111,168,220,0.1)' },
  infrastructure: { color: '#a48fe8', bg: 'rgba(164,143,232,0.1)' },
};

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ManageFunds() {
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [stats, setStats] = useState<Record<string, FundStats>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firm) return;
    fetchFunds();
  }, [firm]);

  const fetchFunds = async () => {
    if (!firm) return;

    const { data: fundsData } = await supabase
      .from('funds')
      .select('*')
      .eq('firm_id', firm.id)
      .order('created_at', { ascending: false });

    if (!fundsData) { setLoading(false); return; }
    setFunds(fundsData);

    // Fetch stats for each fund
    const statsMap: Record<string, FundStats> = {};

    await Promise.all(fundsData.map(async (fund) => {
      // Connection count
      const { count: connCount } = await supabase
        .from('connections')
        .select('*', { count: 'exact', head: true })
        .eq('manager_firm_id', fund.id)
        .eq('status', 'approved');

      // Latest performance
      const { data: perfData } = await supabase
        .from('fund_performance')
        .select('year, month, return_pct')
        .eq('fund_id', fund.id)
        .order('year', { ascending: false })
        .order('month', { ascending: false })
        .limit(1);

      // Total months
      const { count: monthCount } = await supabase
        .from('fund_performance')
        .select('*', { count: 'exact', head: true })
        .eq('fund_id', fund.id);

      const latest = perfData?.[0];
      statsMap[fund.id] = {
        fund_id: fund.id,
        connections: connCount || 0,
        latest_return: latest?.return_pct ?? null,
        latest_month: latest ? `${monthNames[latest.month - 1]} ${latest.year}` : null,
        months_of_data: monthCount || 0,
      };
    }));

    setStats(statsMap);
    setLoading(false);
  };

  const cardStyle = {
    background: '#FFFFFF',
    border: '1px solid #D0D5DD',
    borderRadius: '4px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  };

  if (loading) return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#6B7280' }}>Loading funds...</div>
    </div>
  );

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '26px', color: '#0A0A0A', marginBottom: '4px' }}>
            Manage Funds
          </h1>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>
            {funds.length} fund{funds.length !== 1 ? 's' : ''} · {firm?.name}
          </p>
        </div>
        <button
          onClick={() => navigate('/create-fund')}
          style={{
            background: '#0A1628', border: 'none', borderRadius: '3px',
            color: '#FFFFFF', fontSize: '13px', fontWeight: 600,
            padding: '9px 18px', cursor: 'pointer',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >+ New fund</button>
      </div>

      {funds.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '16px', color: '#0A0A0A', fontFamily: 'Georgia, serif', marginBottom: '8px' }}>
            No funds yet
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px' }}>
            Create your first fund to start connecting with allocators
          </div>
          <button
            onClick={() => navigate('/create-fund')}
            style={{
              background: '#0A1628', border: 'none', borderRadius: '3px',
              color: '#FFFFFF', fontSize: '13px', fontWeight: 600,
              padding: '10px 24px', cursor: 'pointer',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >Create fund</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {funds.map(fund => {
            const cls = classColors[fund.asset_class] || { color: '#6B7280', bg: '#6B728015' };
            const s = stats[fund.id];
            return (
              <div key={fund.id} style={{
                ...cardStyle,
                borderLeft: `3px solid ${cls.color}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <span style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: '#0A0A0A' }}>
                        {fund.name}
                      </span>
                      <span style={{
                        fontSize: '10px', fontWeight: 500, padding: '2px 8px',
                        borderRadius: '20px', background: cls.bg, color: cls.color,
                        letterSpacing: '0.5px', textTransform: 'uppercase'
                      }}>
                        {classLabels[fund.asset_class] || fund.asset_class}
                      </span>
                      <span style={{
                        fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
                        background: fund.status === 'open' ? '#0A7C4215' : '#6B728015',
                        color: fund.status === 'open' ? '#0A7C42' : '#6B7280',
                        textTransform: 'capitalize'
                      }}>
                        {fund.status}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '16px' }}>
                      {fund.strategy || '-'} · Inception {fund.inception_date ? new Date(fund.inception_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-'}
                    </div>

                    {/* Stats row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, auto)', gap: '24px' }}>
                      {[
                        { label: 'AUM', value: fund.aum_min ? `$${fund.aum_min}–${fund.aum_max}mm` : '-' },
                        { label: 'Mgmt fee', value: fund.management_fee ? `${fund.management_fee}%` : '-' },
                        { label: 'Perf fee', value: fund.performance_fee ? `${fund.performance_fee}%` : '-' },
                        { label: 'Min sub', value: fund.minimum_subscription ? `$${fund.minimum_subscription}mm` : '-' },
                        { label: 'Connections', value: s ? String(s.connections) : '-' },
                        {
                          label: 'Latest return',
                          value: s?.latest_return != null
                            ? `${s.latest_return > 0 ? '+' : ''}${s.latest_return.toFixed(2)}% (${s.latest_month})`
                            : 'No data'
                        },
                      ].map((item, i) => (
                        <div key={i}>
                          <div style={{ fontSize: '10.5px', color: '#6B7280', marginBottom: '3px' }}>{item.label}</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A' }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '24px', flexShrink: 0 }}>
                    <button
                      onClick={() => navigate(`/update-performance?fund=${fund.id}`)}
                      style={{
                        background: '#FDF6E7',
                        border: '1px solid #C9A84C',
                        borderRadius: '3px', color: '#C9A84C',
                        fontSize: '12px', padding: '7px 14px',
                        cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                        whiteSpace: 'nowrap'
                      }}
                    >Update performance</button>
                    <button
                      onClick={() => navigate(`/alt-connect/fund/${fund.id}`)}
                      style={{
                        background: '#FFFFFF',
                        border: '1px solid #D0D5DD',
                        borderRadius: '3px', color: '#3D4552',
                        fontSize: '12px', padding: '7px 14px',
                        cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                        whiteSpace: 'nowrap'
                      }}
                    >👁 View profile</button>
                    <button
                      onClick={() => navigate(`/peer-analysis/${fund.id}`)}
                      style={{
                        background: '#FFFFFF',
                        border: '1px solid #0D6E8A',
                        borderRadius: '3px', color: '#0D6E8A',
                        fontSize: '12px', padding: '7px 14px',
                        cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                        whiteSpace: 'nowrap'
                      }}
                    >Peer analysis</button>
                    <div style={{ fontSize: '11px', color: '#6B7280', textAlign: 'center' }}>
                      {s?.months_of_data || 0} months data
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
